/** 逐单归因 —— 受理的每一条信号最后变成了什么、每一笔订单凭什么下、用的是哪一版。
 *
 * 规格 `docs/design/screens/attribution.md`。主表以 `GET /dispositions` 为行（受理信号恰好一行）；
 * 链路分区取 `GET /decisions`（信号 / 研究流程 / 参数集 / 快照 / 风控调整）、`/orders` `/fills` `/positions`
 * 三跳、`GET /snapshots`（快照内容摘要）与 `GET /runs/{run_id}`（配置指纹与 `versions`）。
 * 版本四项（代码 / 依赖 / 配置 / 模型）缺的位显式标「缺」；`model_version` 合约里没有，恒为「缺」。
 */

import { useState } from "react";

import {
  useDecisionsBook,
  useDispositionsBook,
  useFillsBook,
  useOrdersBook,
  usePositionsBook,
  useRunTrace,
  type G2Disposition,
} from "../../api/g2";
import { useSnapshots, useStatus } from "../../api/queries";
import { Card, Kpi, Lede, PageHead } from "../../components/Card";
import { Pill } from "../../components/Pill";
import { QueryState } from "../../components/States";
import { Table } from "../../components/Table";
import { DASH, num, short, ts } from "../../lib/format";
import { ORDERED, buildChain, decisionGap, headCount, versionLinks, type ChainLink } from "../../lib/attribution";
import { BookSelector, InlineError, Missing, useBookParams } from "../orders/Book";

export function AttributionScreen(): React.ReactNode {
  const [book] = useBookParams();
  const status = useStatus();
  const dispositions = useDispositionsBook(book);
  const decisions = useDecisionsBook(book);
  const runId = dispositions.data?.run_id ?? null;
  const run = useRunTrace(runId);
  const [pick, setPick] = useState<string | null>(null);

  if (dispositions.isPending) return <QueryState status="pending" error={null} what="信号下场">{null}</QueryState>;
  if (dispositions.isError) {
    return (
      <>
        <PageHead title="逐单归因" actions={<BookSelector envelope={null} />} />
        <QueryState status="error" error={dispositions.error} what="信号下场（GET /api/v1/dispositions）" refetch={() => void dispositions.refetch()}>
          {null}
        </QueryState>
      </>
    );
  }

  const rows = dispositions.data.rows;
  const count = headCount(run.data?.accepted ?? null, rows);
  const ordered = rows.filter((row) => row.disposition === ORDERED).length;
  const decisionMap = decisions.data?.rows ?? {};
  const gaps = decisions.isSuccess ? rows.filter((row) => decisionGap(row, decisionMap) !== null).length : null;
  const selected = rows.find((row) => row.signal_id === pick) ?? null;
  const neverRan = !book.run_id && status.isSuccess && status.data.current_run === null;

  return (
    <>
      <PageHead
        title="逐单归因"
        sub={
          <span className={count.mismatch ? "neg" : undefined}>
            受理 {count.accepted ?? "?"} 条 · 有下场 {count.dispositions} 条
            {count.mismatch ? "，留痕不完整" : ""}
          </span>
        }
        actions={<BookSelector envelope={dispositions.data} />}
      />
      <Lede>
        受理的每一条信号恰好一行：<b>已下单</b>带订单号，没下单的写明六种原因之一（窗口未到 / 窗口已过 / 目标未变 / 停机停手 /
        数量归零 / 风控拒单）。选一行看链路：信号 → 研究流程与参数 → 数据快照 → 风控 → 订单 → 成交 → 仓位 → 版本。
      </Lede>

      <div className="grid g4" style={{ marginBottom: 12 }}>
        <Kpi label="受理信号" value={count.accepted === null ? <Missing why="运行归档未取到" /> : num(count.accepted, 0)} detail="GET /runs/{run_id} · signals.accepted" />
        <Kpi label="有下场" value={num(count.dispositions, 0)} detail="GET /dispositions" />
        <Kpi label="已下单" value={num(ordered, 0)} detail={`其余 ${rows.length - ordered} 条写明未下单原因`} />
        <Kpi
          label="有订单无决策留痕"
          value={gaps === null ? <span className="faint">未取到</span> : <span className={gaps ? "neg" : "dim"}>{num(gaps, 0)}</span>}
          detail="GET /decisions"
        />
      </div>
      {decisions.isError ? (
        <div style={{ marginBottom: 12 }}>
          <InlineError endpoint="GET /api/v1/decisions" error={decisions.error} onRetry={() => void decisions.refetch()} />
        </div>
      ) : null}

      <div className="split wide">
        <Card title="信号下场" nopad>
          <Table
            cols={[
              { label: "信号", mono: true },
              { label: "标的" },
              { label: "下场" },
              { label: "订单", mono: true },
              { label: "说明", wrap: true },
            ]}
            rows={rows.map((row) => {
              const gap = decisions.isSuccess ? decisionGap(row, decisionMap) : null;
              return {
                key: row.signal_id,
                cls: gap ? "warnrow" : "",
                selected: row.signal_id === pick,
                onClick: () => setPick(row.signal_id),
                cells: [
                  <span className="mono">{short(row.signal_id, 18)}</span>,
                  row.instrument_id || DASH,
                  <Pill tone={row.disposition === ORDERED ? "ok" : row.disposition === "停机停手" || row.disposition === "风控拒单" ? "bad" : "neutral"}>
                    {row.disposition}
                  </Pill>,
                  row.order_id ? <a className="mono" href={`#orders?id=${encodeURIComponent(row.order_id)}`}>{short(row.order_id, 18)}</a> : DASH,
                  gap ? <span className="neg">{gap}</span> : row.disposition === "停机停手" ? <a href="#halt">去停机屏</a> : row.detail ?? "",
                ],
              };
            })}
            empty={
              neverRan
                ? "还没有任何运行，没有可归因的信号"
                : "该运行没有受理任何信号（当日无信号或全部被拒收，见收件箱 / 拒收台账）"
            }
            tall
          />
        </Card>
        <ChainCard row={selected} runId={runId} />
      </div>
      <div className="hint" style={{ marginTop: 8 }}>
        信号受理前的拒收在<a href="#rejected">拒收台账</a>；按日回放在<a href="#replay">运行与回放</a>。
        {run.isError ? <span className="neg"> 运行归档未取到（受理计数与版本环缺）。</span> : null}
      </div>
    </>
  );
}

function ChainCard({ row, runId }: { row: G2Disposition | null; runId: string | null }): React.ReactNode {
  const [book] = useBookParams();
  const decisions = useDecisionsBook(book);
  const orders = useOrdersBook(book);
  const fills = useFillsBook(book);
  const positions = usePositionsBook(book);
  const snapshots = useSnapshots();
  const run = useRunTrace(runId);

  if (!row) {
    return (
      <Card title="链路">
        <div className="empty">选一条信号看链路</div>
      </Card>
    );
  }
  const failed = [
    ["GET /api/v1/decisions", decisions],
    ["GET /api/v1/orders", orders],
    ["GET /api/v1/fills", fills],
    ["GET /api/v1/positions", positions],
  ] as const;
  const firstError = failed.find(([, q]) => q.isError);
  const ready = decisions.data && orders.data && fills.data && positions.data;
  const links = ready
    ? buildChain(row, {
        decisions: decisions.data.rows,
        orders: orders.data.rows,
        trades: fills.data.rows,
        positions: positions.data.rows,
      })
    : [];
  const snapshotId = row.order_id ? decisions.data?.rows[row.order_id]?.data_snapshot : undefined;
  const snapshot = snapshotId ? snapshots.data?.snapshots.find((item) => item.snapshot_id === snapshotId) : undefined;

  return (
    <Card title="链路" sub={row.signal_id}>
      {firstError ? (
        <InlineError endpoint={firstError[0]} error={firstError[1].error} onRetry={() => void firstError[1].refetch()} />
      ) : null}
      {!ready && !firstError ? <div className="faint">取数中…</div> : null}
      <ChainList links={links} />
      {snapshotId ? (
        <div className="hint" style={{ margin: "6px 0" }}>
          {snapshots.isError ? (
            <span className="neg">快照目录未取到（GET /snapshots）</span>
          ) : snapshot ? (
            <>
              快照内容：sha256 {short(snapshot.content_sha256, 12)} · {num(snapshot.row_count, 0)} 行 · {ts(snapshot.min_ts_ns)} ～ {ts(snapshot.max_ts_ns)} · {snapshot.source}
            </>
          ) : snapshots.isSuccess ? (
            <span className="warn">快照 {snapshotId} 未登记（无法确认内容摘要）</span>
          ) : null}
        </div>
      ) : null}
      <div className="hint" style={{ margin: "8px 0 4px" }}>版本（GET /runs/{"{run_id}"}）</div>
      {run.isError ? (
        <InlineError endpoint={`GET /api/v1/runs/${runId}`} error={run.error} onRetry={() => void run.refetch()} />
      ) : (
        <ChainList links={versionLinks(run.data ?? null)} />
      )}
    </Card>
  );
}

function ChainList({ links }: { links: ChainLink[] }): React.ReactNode {
  return (
    <dl className="kv">
      {links.map((link) => (
        <div key={link.label} style={{ display: "contents" }}>
          <dt>{link.label}</dt>
          <dd className={link.muted ? "faint" : undefined}>{link.value ?? <Missing why={link.why} />}</dd>
        </div>
      ))}
    </dl>
  );
}
